import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteField,
  orderBy,
  limit,
  startAfter,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { callFunction } from "./callFunction";
import Utility from "../utils/utility";
import { showToast } from "../utils/showToast";
import MatchApi from "./MatchApi";
import { mergeEventReports } from "../utils/eventReports";

class EventAPI {
  static collectionName = "events";

  static async getAllEvents() {
    try {
      const querySnapshot = await getDocs(
        collection(db, EventAPI.collectionName)
      );
      const events = [];
      querySnapshot.forEach((doc) =>
        events.push({ ...doc.data(), id: doc.id })
      );
      return events;
    } catch (error) {
      throw error;
    }
  }
  static getEventStreaming(eventId, callback) {
    try {
      const docRef = doc(db, EventAPI.collectionName, eventId);
      return onSnapshot(docRef, (doc) => {
        if (doc.exists()) {
          callback({ ...doc.data(), id: doc.id });
        } else {
          console.warn("No such document!");
        }
      });
    } catch (error) {
      throw error;
    }
  }
  static getEventStreamingWithGroupChatCount(
    eventId,
    subCollectionName,
    timestamp,
    callback
  ) {
    try {
      const docRef = doc(db, EventAPI.collectionName, eventId);

      return onSnapshot(docRef, (doc) => {
        if (doc.exists()) {
          const eventData = { ...doc.data(), id: doc.id };

          const subCollectionRef = collection(
            db,
            EventAPI.collectionName,
            eventId,
            subCollectionName
          );

          if (!(timestamp instanceof Date)) {
            timestamp = Utility.convertDate(timestamp);
          }
          const q = query(subCollectionRef, where("timestamp", ">", timestamp));

          onSnapshot(q, (querySnapshot) => {
            const subCollectionCount = querySnapshot.size;
            callback({
              ...eventData,
              [`${subCollectionName}Count`]: subCollectionCount,
            });
          });
        } else {
          console.warn("No such document!");
        }
      });
    } catch (error) {
      throw error;
    }
  }
  static async getEventsFor(userID, gender) {
    try {
      const propertyName = Utility.getGenderTypeFromGender(gender);
      const eventsQuery = query(
        collection(db, EventAPI.collectionName),
        where(propertyName, "array-contains", userID)
      );
      const querySnap = await getDocs(eventsQuery);
      const events = [];
      querySnap.forEach((doc) => events.push({ ...doc.data(), id: doc.id }));
      return events;
    } catch (err) {
      console.error(err);
    }
  }

  static async addUserToEvent(eventId, userId, gender) {
    const eventRef = doc(db, EventAPI.collectionName, eventId);
    const eventDoc = await getDoc(eventRef);
    const propertyName = Utility.getGenderTypeFromGender(gender);

    if (eventDoc.exists()) {
      const eventData = eventDoc.data();
      const users = eventData[propertyName] || [];

      if (!users.includes(userId)) {
        await updateDoc(eventRef, {
          [propertyName]: arrayUnion(userId),
        });
      }
    } else {
      console.warn(`Event ${eventId} does not exist.`);
    }
  }

  static async removeUserFromEvent(eventId, userId, gender) {
    const propertyName = Utility.getGenderTypeFromGender(gender);
    const eventRef = doc(db, EventAPI.collectionName, eventId);
    const eventDoc = await getDoc(eventRef);

    if (eventDoc.exists()) {
      const eventData = eventDoc.data();
      const users = eventData[propertyName] || [];

      if (users.includes(userId)) {
        await updateDoc(eventRef, {
          [propertyName]: arrayRemove(userId),
        });
      }
    } else {
      console.warn(`Event ${eventId} does not exist.`);
    }
  }

  static async lastCheckedChat(eventID, userID) {
    const lastCheckedChatTS = {
      [`lastCheckedChat.${userID}`]: serverTimestamp(),
    };
    await EventAPI.updateEvent(eventID, lastCheckedChatTS);
  }

  static async updateEvent(eventID, valueObject) {
    try {
      const eventRef = doc(db, EventAPI.collectionName, eventID);

      // Fetch the document
      const docSnap = await getDoc(eventRef);

      if (!docSnap.exists()) {
        console.warn("No matching event found.");
        return null;
      }

      // Update the document
      await updateDoc(eventRef, valueObject);

      // Fetch the updated document
      const updatedDoc = await getDoc(eventRef);
      const updatedEvent = updatedDoc.data();

      showToast("Event updated successfully");
      return updatedEvent;
    } catch (error) {
      console.error("Error updating event: ", error);
      throw error;
    }
  }

  // A fresh auto-id for an event that doesn't exist yet. Generated client-side
  // so the create can write the doc's own `id` field atomically, and photo
  // upload paths can use the id before the doc exists.
  static newEventId() {
    return doc(collection(db, EventAPI.collectionName)).id;
  }

  // Create the event doc under an id from newEventId(), stamping createdAt with
  // the server time. A plain setDoc: no read-back and no toast, unlike updateEvent.
  static async createEvent(eventID, data) {
    await setDoc(doc(db, EventAPI.collectionName, eventID), {
      ...data,
      createdAt: serverTimestamp(),
    });
  }

  // Plain field update on an event doc: no existence read and no toast.
  static async patchEvent(eventID, fields) {
    await updateDoc(doc(db, EventAPI.collectionName, eventID), fields);
  }

  // Take a guest off an event: out of the RSVP and check-in lists for their
  // side, and their eventUsers entry dropped. Atomic per-field ops, never
  // full-array (or full-map) rewrites: this races live RSVP/check-in writes, and
  // rewriting whole arrays from a held copy of the event would clobber anyone
  // who joined since. deleteField on an absent entry is a no-op, so the
  // eventUsers removal needs no existence check.
  static async removeGuest(eventID, userID, userType) {
    const updates = {
      [`eventUsers.${userID}`]: deleteField(),
    };
    if (userType === "movers") {
      updates.rsvpMovers = arrayRemove(userID);
      updates.movers = arrayRemove(userID);
    } else if (userType === "sitters") {
      updates.rsvpSitters = arrayRemove(userID);
      updates.sitters = arrayRemove(userID);
    }
    await updateDoc(doc(db, EventAPI.collectionName, eventID), updates);
  }

  static async deleteFieldProperty(documentId, fields) {
    // Reference to the specific document
    const docRef = doc(db, EventAPI.collectionName, documentId);

    // Update the document, deleting the specified field
    const updateObject = {};
    fields.forEach((field) => {
      updateObject[field] = deleteField();
    });

    await updateDoc(docRef, updateObject);

    // console.log(`Field "${field}" has been deleted from document "${documentId}" in collection "${collection}".`);
  }
  static getEventStreamingWithGroupChatData(
    eventId,
    subCollectionName,
    timestamp,
    callback
  ) {
    try {
      const docRef = doc(db, EventAPI.collectionName, eventId);

      return onSnapshot(docRef, (doc) => {
        if (doc.exists()) {
          const eventData = { ...doc.data(), id: doc.id };

          const subCollectionRef = collection(
            db,
            EventAPI.collectionName,
            eventId,
            subCollectionName
          );

          if (!(timestamp instanceof Date)) {
            timestamp = Utility.convertDate(timestamp);
          }
          const q = query(subCollectionRef, where("timestamp", ">", timestamp));

          onSnapshot(q, (querySnapshot) => {
            const subCollectionData = querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            callback({
              ...eventData,
              [subCollectionName]: subCollectionData,
            });
          });
        } else {
          console.warn("No such document!");
        }
      });
    } catch (error) {
      throw error;
    }
  }
  static async sendMessage(eventId, subCollectionName, message, userId, userName, type, notifyTarget, attachments) {
    const messageObj = { message, userID: userId, name: userName, ...(type && { type }) };
    if (notifyTarget) {
      messageObj.notifyTarget = notifyTarget;
    }
    if (attachments && attachments.length > 0) {
      messageObj.attachments = attachments;
    }
    await MatchApi.addMessageToChat(eventId, subCollectionName, messageObj, EventAPI.collectionName);
  }

  static async addReaction(eventId, subCollectionName, messageId, emoji, userID) {
    const messageRef = doc(db, EventAPI.collectionName, eventId, subCollectionName, messageId);
    await updateDoc(messageRef, {
      [`reactions.${emoji}`]: arrayUnion(userID)
    });
  }

  static async removeReaction(eventId, subCollectionName, messageId, emoji, userID) {
    const messageRef = doc(db, EventAPI.collectionName, eventId, subCollectionName, messageId);
    await updateDoc(messageRef, {
      [`reactions.${emoji}`]: arrayRemove(userID)
    });
  }

  static async softDeleteMessage(eventId, subCollectionName, messageId) {
    const messageRef = doc(db, EventAPI.collectionName, eventId, subCollectionName, messageId);
    await updateDoc(messageRef, { deleted: true });
  }

  static async permanentlyDeleteMessage(eventId, subCollectionName, messageId) {
    const messageRef = doc(db, EventAPI.collectionName, eventId, subCollectionName, messageId);
    await deleteDoc(messageRef);
  }

  // Safety reports filed at an event: its arbitration cases, plus any report
  // still sitting on the event doc from before the server started moving them
  // off it (see utils/eventReports.js). Returns { reports, restricted }:
  // restricted is true when this admin can't read arbitrations (they need the
  // superAdmin claim; the vendors isSuperAdmin flag alone opens this tab but not
  // the cases), so the tab can say so instead of looking empty.
  static async fetchReportsByEventID(eventID) {
    try {
      const eventRef = doc(db, EventAPI.collectionName, eventID);
      let restricted = false;
      const [eventDoc, cases] = await Promise.all([
        getDoc(eventRef),
        getDocs(query(collection(db, "arbitrations"), where("eventId", "==", eventID)))
          .then((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
          .catch((error) => {
            if (error?.code === "permission-denied") restricted = true;
            else console.error("Error fetching arbitration cases for event: ", error);
            return [];
          }),
      ]);
      const legacy = eventDoc.exists() ? eventDoc.data().reports : [];
      return { reports: mergeEventReports(cases, legacy), restricted };
    } catch (error) {
      console.error("Error fetching reports: ", error);
      throw error;
    }
  }

  static async fetchReviewsByEventID(eventID) {
    try {
      const eventRef = doc(db, EventAPI.collectionName, eventID);
      const eventDoc = await getDoc(eventRef);

      if (!eventDoc.exists()) {
        console.warn("No matching event found.");
        return null;
      }

      const eventData = eventDoc.data();
      const reviews = eventData.review || [];
      return reviews;
    } catch (error) {
      console.error("Error fetching reviews: ", error);
      throw error;
    }
  }

  static async getActiveEventsByUserID(userID) {
    try {
      const eventsCollection = collection(db, EventAPI.collectionName);
      const q = query(
        eventsCollection,
        where("isActive", "==", true)
      );
      const querySnapshot = await getDocs(q);

      const activeEvents = [];
      querySnapshot.forEach((doc) => {
        const eventData = doc.data();
        // Check if user exists in eventUsers
        if (eventData.eventUsers && eventData.eventUsers[userID]) {
          activeEvents.push({ ...eventData, id: doc.id });
        }
      });

      return activeEvents;
    } catch (error) {
      console.error("Error fetching active events by userID: ", error);
      throw error;
    }
  }

  static async updateUserInActiveEvents(userID, name, photoURL) {
    try {
      const activeEvents = await EventAPI.getActiveEventsByUserID(userID);

      if (activeEvents.length === 0) {
        return;
      }

      // Update each event with the new user data
      const updatePromises = activeEvents.map((event) => {
        const eventRef = doc(db, EventAPI.collectionName, event.id);
        const updateData = {
          [`eventUsers.${userID}.name`]: name,
          [`eventUsers.${userID}.photoURL`]: photoURL,
        };
        return updateDoc(eventRef, updateData);
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating user in active events: ", error);
      throw error;
    }
  }

  static async getPaginatedMessages(parentCollection, parentID, subCollectionName, limitCount = 25, lastVisible = null) {
    try {
      const subCollectionRef = collection(db, parentCollection, parentID, subCollectionName);
      let q;
      if (lastVisible) {
        q = query(
          subCollectionRef,
          orderBy("timestamp", "desc"),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        q = query(
          subCollectionRef,
          orderBy("timestamp", "desc"),
          limit(limitCount)
        );
      }

      const querySnapshot = await getDocs(q);
      const messages = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const ts = data.timestamp && typeof data.timestamp.toMillis === "function"
          ? data.timestamp.toMillis()
          : (typeof data.timestamp === "number" ? data.timestamp : Date.now());
        return {
          id: doc.id,
          ...data,
          timestamp: ts
        };
      });
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

      return { messages, lastDoc, hasMore: querySnapshot.docs.length === limitCount };
    } catch (error) {
      console.error("Error fetching paginated messages:", error);
      throw error;
    }
  }

  static async getProcessedPayments(eventID) {
    try {
      const snap = await getDocs(
        query(
          collection(db, EventAPI.collectionName, eventID, "processedPayments"),
          orderBy("processedAt", "desc")
        )
      );
      return snap.docs.map((doc) => doc.data());
    } catch (error) {
      console.error("Error fetching processed payments:", error);
      throw error;
    }
  }

  // GIS-181 Track B: event revenue moved off the broadly-readable event doc into
  // the host/admin-gated events/{id}/finance/summary doc. Single-event read for the
  // EventForm Revenue tab (rules allow the event host + admin). null when absent.
  static async getRevenue(eventID) {
    try {
      const snap = await getDoc(doc(db, EventAPI.collectionName, eventID, "finance", "summary"));
      return snap.exists() ? (snap.data().revenue || null) : null;
    } catch (error) {
      console.error("Error fetching event revenue:", error);
      return null;
    }
  }

  // Batch revenue for the Events list — a bulk list query can't join the per-event
  // finance doc, so go through the host/superAdmin-scoped getEventsRevenue callable.
  // Returns { [eventId]: { total, tickets, prioritizations } } for allowed events.
  static async getEventsRevenue(eventIds) {
    try {
      if (!Array.isArray(eventIds) || eventIds.length === 0) return {};
      const res = await callFunction("getEventsRevenue", { eventIds });
      return (res && res.revenue) || {};
    } catch (error) {
      console.error("Error fetching events revenue:", error);
      return {};
    }
  }

  // Refund ledger entries, keyed by paymentIntentId. A doc exists once a refund
  // has been initiated (status "processing") and is finalized by the Stripe
  // webhook (status "refunded", amountRefundedCents set).
  static async getRefundedPayments(eventID) {
    try {
      const snap = await getDocs(
        collection(db, EventAPI.collectionName, eventID, "refundedPayments")
      );
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      return map;
    } catch (error) {
      console.error("Error fetching refunded payments:", error);
      throw error;
    }
  }

  // Cancel an event. Blocked by the backend while any paid ticket is unrefunded
  // (the wizard refunds first). `reason` is stored internally; `note`, when set,
  // is the exact message attendees receive by SMS + push.
  static async cancelEvent(eventID, { reason = null, note = null } = {}) {
    try {
      return await callFunction("cancelEvent", { eventID, reason, note }, { timeout: 60000 });
    } catch (error) {
      console.error("Error calling cancelEvent function:", error);
      throw error;
    }
  }

  // Co-hosts (GIS-222). coHostIDs is server-only in the rules, so every change
  // goes through these callables. They throw with a message the UI can show.
  static async addEventCoHost(eventID, coHostID) {
    return callFunction("addEventCoHost", { eventID, coHostID }, { timeout: 60000 });
  }

  static async removeEventCoHost(eventID, coHostID) {
    return callFunction("removeEventCoHost", { eventID, coHostID }, { timeout: 60000 });
  }

  // Events the signed-in host co-hosts. A single array-contains filter, so it
  // needs no composite index; the list splits them into tabs client-side.
  static async getCoHostedEvents(uid) {
    try {
      if (!uid) return [];
      const snap = await getDocs(
        query(collection(db, EventAPI.collectionName), where("coHostIDs", "array-contains", uid), limit(200))
      );
      return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
    } catch (error) {
      console.error("Error fetching co-hosted events:", error);
      return [];
    }
  }

  // Issue a full refund for a single ticket/boost purchase. Backend gates this to
  // the event's host or a super admin and notifies the buyer.
  static async refundEventTicket(eventID, paymentIntentId, reason) {
    try {
      return await callFunction("refundEventTicket", { eventID, paymentIntentId, reason });
    } catch (error) {
      console.error("Error calling refundEventTicket function:", error);
      throw error;
    }
  }

  // Email-related methods
  static subscribeToEmails(eventID, callback) {
    try {
      const q = query(
        collection(db, "events", eventID, "email"),
        orderBy("sentAt", "desc")
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const emailList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(emailList);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error subscribing to emails:", error);
      throw error;
    }
  }

  static async sendEmail(to, subject, body, eventID, userName) {
    try {
      return await callFunction("sendEmail", {
        to: to,
        subject: subject,
        html: `<p>${body.replace(/\n/g, "<br/>")}</p>`,
        eventID: eventID,
        name: userName,
        direction: "sent"
      });
    } catch (error) {
      console.error("Error calling sendEmail function:", error);
      throw error;
    }
  }

  static async deleteEmail(eventID, emailID) {
    try {
      await deleteDoc(doc(db, "events", eventID, "email", emailID));
    } catch (error) {
      console.error("Error deleting email:", error);
      throw error;
    }
  }

  static async saveAIAgentPrompt(eventID, aiAgentPrompt) {
    try {
      await updateDoc(doc(db, "events", eventID), {
        "email.ai_agent": aiAgentPrompt
      });
    } catch (error) {
      console.error("Error saving AI Agent prompt:", error);
      throw error;
    }
  }

  static async getAIAgentPrompt(eventID) {
    try {
      const eventRef = doc(db, "events", eventID);
      const eventDoc = await getDoc(eventRef);

      if (!eventDoc.exists()) {
        console.warn("No matching event found.");
        return null;
      }

      const eventData = eventDoc.data();
      return eventData.email?.ai_agent || "";
    } catch (error) {
      console.error("Error getting AI Agent prompt:", error);
      throw error;
    }
  }

  static async generateEmailWithAI(eventID, instructions, eventData, userName) {
    try {
      return await callFunction("generateEmailWithAI", {
        mode: "assistant",
        eventID: eventID,
        instructions: instructions,
        eventData: eventData,
        userName: userName
      });
    } catch (error) {
      console.error("Error in AI Assistant:", error);
      throw error;
    }
  }

}

export default EventAPI;
